import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { formatDisplayDate } from "@/lib/dateFormat";
import { useAuth } from "@/context/AuthContext";
import AdminLayout from "@/components/AdminLayout";
import {
  Plus,
  Laptop,
  Search,
  Pencil,
  Trash2,
  UserPlus,
  UserMinus,
  Smartphone,
  CreditCard,
  KeyRound,
  Headphones,
  Package,
} from "lucide-react";
import { SortableTH, useSortableData, EmptyState } from "@/components/SortableTable";
import TableSkeleton from "@/components/TableSkeleton";
import RowActionsMenu from "@/components/RowActionsMenu";
import Banner from "@/components/Banner";

const ASSET_TYPES = [
  "Laptop",
  "Desktop",
  "Monitor",
  "Mobile Phone",
  "SIM Card",
  "ID Card",
  "Access Card",
  "Headphone",
  "Keyboard",
  "Mouse",
  "Tablet",
  "Charger",
  "Other",
];

const ASSET_STATUSES = ["Available", "Assigned", "Retired", "Lost", "Under Repair"];

const STATUS_COLORS = {
  Available: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Assigned: "bg-sky-100 text-sky-700 border-sky-200",
  Retired: "bg-slate-100 text-slate-600 border-slate-200",
  Lost: "bg-rose-100 text-rose-700 border-rose-200",
  "Under Repair": "bg-amber-100 text-amber-700 border-amber-200",
};

const TYPE_ICON = {
  Laptop: Laptop,
  Desktop: Laptop,
  Monitor: Laptop,
  "Mobile Phone": Smartphone,
  Tablet: Smartphone,
  "SIM Card": CreditCard,
  "ID Card": CreditCard,
  "Access Card": KeyRound,
  Headphone: Headphones,
};

const inputCls =
  "w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#226DB4]/30 focus:border-[#226DB4] bg-white";

const EMPTY_FORM = {
  name: "",
  asset_type: "Laptop",
  serial_number: "",
  status: "Available",
  notes: "",
  assigned_to_employee_id: "",
  assigned_date: "",
};

const StatCard = ({ label, value, tone = "slate", testid }) => {
  const map = {
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    sky: "bg-sky-50 text-sky-700 border-sky-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
  };
  return (
    <div
      data-testid={testid}
      className={`border rounded px-4 py-3 ${map[tone]}`}
    >
      <div className="text-[11px] uppercase tracking-wider font-semibold opacity-80">
        {label}
      </div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
};

export default function Assets() {
  const { user } = useAuth();
  const canDelete = user?.role === "admin";

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All");

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const [assignFor, setAssignFor] = useState(null); // asset to assign
  const [assignForm, setAssignForm] = useState({ employee_id: "", assigned_date: "" });

  const loadAll = async () => {
    setLoading(true);
    try {
      const [a, s, e] = await Promise.all([
        api.get("/assets"),
        api.get("/assets/stats/summary").catch(() => ({ data: null })),
        api.get("/employees").catch(() => ({ data: [] })),
      ]);
      setItems(a.data || []);
      setStats(s.data);
      setEmployees(e.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  // ── Sidebar-driven action (?action=add) ──
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const action = searchParams.get("action");
    if (!action) return;
    if (action === "add") {
      setEditing(null);
      setForm(EMPTY_FORM);
      setErr("");
      setOpen(true);
    }
    const next = new URLSearchParams(searchParams);
    next.delete("action");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const filtered = useMemo(() => {
    let list = items;
    if (statusFilter !== "All") list = list.filter((a) => a.status === statusFilter);
    if (typeFilter !== "All") list = list.filter((a) => a.asset_type === typeFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (a) =>
          (a.name || "").toLowerCase().includes(q) ||
          (a.serial_number || "").toLowerCase().includes(q) ||
          (a.asset_type || "").toLowerCase().includes(q) ||
          (a.assigned_to?.name || "").toLowerCase().includes(q) ||
          (a.assigned_to?.code || "").toLowerCase().includes(q),
      );
    }
    return list;
  }, [items, statusFilter, typeFilter, search]);

  // Phase 14 — sortable
  const { items: sortedFiltered, sortBy, sortDir, requestSort } = useSortableData(filtered, {
    initialKey: "name",
    accessors: {
      assigned_to: (r) => r.assigned_to?.name || "",
      assigned_date: (r) => r.assigned_date || "",
    },
  });

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setErr("");
    setOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      name: a.name || "",
      asset_type: a.asset_type || "Other",
      serial_number: a.serial_number || "",
      status: a.status || "Available",
      notes: a.notes || "",
      assigned_to_employee_id: a.assigned_to_employee_id || "",
      assigned_date: a.assigned_date || "",
    });
    setErr("");
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSaving(true);
    try {
      const payload = { ...form };
      // Normalize empties
      if (!payload.assigned_to_employee_id) {
        payload.assigned_to_employee_id = null;
        payload.assigned_date = null;
      }
      if (!payload.assigned_date) payload.assigned_date = null;
      if (editing) {
        await api.put(`/assets/${editing.id}`, payload);
      } else {
        await api.post("/assets", payload);
      }
      setOpen(false);
      loadAll();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (a) => {
    if (!window.confirm(`Delete asset "${a.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/assets/${a.id}`);
      loadAll();
    } catch (ex) {
      alert(ex?.response?.data?.detail || "Delete failed");
    }
  };

  const openAssign = (a) => {
    setAssignFor(a);
    setAssignForm({ employee_id: "", assigned_date: new Date().toISOString().slice(0, 10) });
  };

  const submitAssign = async (e) => {
    e.preventDefault();
    if (!assignForm.employee_id) return;
    try {
      await api.post(`/assets/${assignFor.id}/assign`, assignForm);
      setAssignFor(null);
      loadAll();
    } catch (ex) {
      alert(ex?.response?.data?.detail || "Assign failed");
    }
  };

  const onUnassign = async (a) => {
    if (!window.confirm(`Unassign "${a.name}" from ${a.assigned_to?.name}?`)) return;
    try {
      await api.post(`/assets/${a.id}/unassign`);
      loadAll();
    } catch (ex) {
      alert(ex?.response?.data?.detail || "Unassign failed");
    }
  };

  return (
    <AdminLayout
      title="Asset Management"
      breadcrumbs={["Home", "Organization", "Asset Management"]}
    >
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="asset-stats">
        <StatCard label="Total Assets" value={stats?.total ?? "—"} tone="slate" testid="asset-stat-total" />
        <StatCard label="Assigned" value={stats?.assigned ?? "—"} tone="sky" testid="asset-stat-assigned" />
        <StatCard label="Available" value={stats?.available ?? "—"} tone="emerald" testid="asset-stat-available" />
        <StatCard label="Under Repair" value={stats?.under_repair ?? "—"} tone="amber" testid="asset-stat-repair" />
        <StatCard label="Retired / Lost" value={(stats?.retired ?? 0) + (stats?.lost ?? 0)} tone="rose" testid="asset-stat-retired" />
      </div>

      <div className="bg-white border border-slate-200 rounded shadow-sm">
        <div className="px-5 py-3.5 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
            <Package className="h-4 w-4 text-[#226DB4]" /> All Assets
          </h3>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                data-testid="asset-search"
                placeholder="Search name, serial, employee…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="border border-slate-200 rounded pl-8 pr-3 py-1.5 text-sm w-56"
              />
            </div>
            <select
              data-testid="asset-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-slate-200 rounded px-2 py-1.5 text-sm bg-white"
            >
              <option value="All">All Statuses</option>
              {ASSET_STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <select
              data-testid="asset-type-filter"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="border border-slate-200 rounded px-2 py-1.5 text-sm bg-white"
            >
              <option value="All">All Types</option>
              {ASSET_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <button
              data-testid="add-asset-btn"
              onClick={openCreate}
              className="hidden"
            >
              <Plus className="h-4 w-4" /> Add Asset
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="assets-table">
            <thead className="bg-slate-50">
              <tr>
                <SortableTH columnKey="name" active={sortBy} dir={sortDir} onClick={requestSort}>Asset</SortableTH>
                <SortableTH columnKey="asset_type" active={sortBy} dir={sortDir} onClick={requestSort}>Type</SortableTH>
                <SortableTH columnKey="serial_number" active={sortBy} dir={sortDir} onClick={requestSort}>Serial / ID</SortableTH>
                <SortableTH columnKey="status" active={sortBy} dir={sortDir} onClick={requestSort}>Status</SortableTH>
                <SortableTH columnKey="assigned_to" active={sortBy} dir={sortDir} onClick={requestSort}>Assigned To</SortableTH>
                <SortableTH columnKey="assigned_date" active={sortBy} dir={sortDir} onClick={requestSort}>Assigned Date</SortableTH>
                <th className="text-right px-4 py-3 text-xs uppercase tracking-wider text-slate-600 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <TableSkeleton rows={5} cols={7} />}
              {!loading && sortedFiltered.length === 0 && (
                <EmptyState colSpan={7} message="No assets found. Use the sidebar “Add Asset” to create one." />
              )}
              {!loading && sortedFiltered.map((a) => {
                const Icon = TYPE_ICON[a.asset_type] || Package;
                return (
                  <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`asset-row-${a.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-800">
                      <div className="flex items-center gap-2.5">
                        <div className="h-8 w-8 rounded bg-slate-100 flex items-center justify-center text-slate-500">
                          <Icon className="h-4 w-4" />
                        </div>
                        <div>
                          <div className="text-slate-800">{a.name}</div>
                          {a.notes && <div className="text-xs text-slate-500 mt-0.5">{a.notes}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{a.asset_type}</td>
                    <td className="px-4 py-3 text-slate-600 font-mono text-xs">{a.serial_number || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium ${STATUS_COLORS[a.status] || STATUS_COLORS.Available}`}>
                        {a.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {a.assigned_to ? (
                        <div>
                          <div className="text-sm font-medium">{a.assigned_to.name}</div>
                          <div className="text-xs text-slate-500">{a.assigned_to.code}</div>
                        </div>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{formatDisplayDate(a.assigned_date)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end">
                        <RowActionsMenu
                          testid={`asset-actions-${a.id}`}
                          items={[
                            { label: "Assign", icon: UserPlus, onClick: () => openAssign(a), hidden: a.status === "Assigned" || a.status === "Retired" || a.status === "Lost", testid: `asset-assign-${a.id}` },
                            { label: "Unassign", icon: UserMinus, onClick: () => onUnassign(a), hidden: a.status !== "Assigned", testid: `asset-unassign-${a.id}` },
                            { label: "Edit", icon: Pencil, onClick: () => openEdit(a), testid: `asset-edit-${a.id}` },
                            { label: "Delete", icon: Trash2, danger: true, onClick: () => onDelete(a), hidden: !canDelete, testid: `asset-delete-${a.id}` },
                          ]}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create / Edit modal */}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <form
            onSubmit={submit}
            className="bg-white rounded-lg shadow-xl w-full max-w-xl p-6 space-y-4"
            data-testid="asset-form"
          >
            <h3 className="text-lg font-semibold text-slate-800">
              {editing ? "Edit Asset" : "Add New Asset"}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">
                  Asset Name <span className="text-rose-500">*</span>
                </label>
                <input
                  required
                  data-testid="asset-name-input"
                  className={inputCls}
                  placeholder="e.g. Dell Latitude 5430"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">
                  Type
                </label>
                <select
                  data-testid="asset-type-input"
                  className={inputCls}
                  value={form.asset_type}
                  onChange={(e) => setForm({ ...form, asset_type: e.target.value })}
                >
                  {ASSET_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">
                  Serial Number / Tag
                </label>
                <input
                  data-testid="asset-serial-input"
                  className={inputCls}
                  placeholder="e.g. DL5430-001"
                  value={form.serial_number}
                  onChange={(e) => setForm({ ...form, serial_number: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">
                  Status
                </label>
                <select
                  data-testid="asset-status-input"
                  className={inputCls}
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  {ASSET_STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">
                  Assign To (optional)
                </label>
                <select
                  data-testid="asset-assignee-input"
                  className={inputCls}
                  value={form.assigned_to_employee_id}
                  onChange={(e) => setForm({ ...form, assigned_to_employee_id: e.target.value })}
                >
                  <option value="">— None —</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.basic?.code} · {[emp.basic?.first_name, emp.basic?.middle_name, emp.basic?.last_name].filter(Boolean).join(" ")}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">
                  Assigned Date
                </label>
                <input
                  type="date"
                  data-testid="asset-assigned-date-input"
                  className={inputCls}
                  value={form.assigned_date}
                  onChange={(e) => setForm({ ...form, assigned_date: e.target.value })}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">
                  Notes
                </label>
                <textarea
                  data-testid="asset-notes-input"
                  className={inputCls}
                  rows={2}
                  placeholder="Condition, accessories, etc."
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                />
              </div>
            </div>
            <Banner variant="error">{err}</Banner>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="px-4 py-2 text-sm border border-slate-200 rounded"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                data-testid="asset-save-btn"
                className="px-4 py-2 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded disabled:opacity-70"
              >
                {saving ? "Saving…" : editing ? "Save Changes" : "Create Asset"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Assign modal */}
      {assignFor && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <form
            onSubmit={submitAssign}
            className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4"
            data-testid="asset-assign-form"
          >
            <h3 className="text-lg font-semibold text-slate-800">Assign Asset</h3>
            <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded p-3">
              <div className="font-medium text-slate-800">{assignFor.name}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {assignFor.asset_type} {assignFor.serial_number && `· ${assignFor.serial_number}`}
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">
                Employee <span className="text-rose-500">*</span>
              </label>
              <select
                required
                data-testid="assign-employee-select"
                className={inputCls}
                value={assignForm.employee_id}
                onChange={(e) => setAssignForm({ ...assignForm, employee_id: e.target.value })}
              >
                <option value="">Select Employee</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.basic?.code} · {emp.basic?.first_name} {emp.basic?.last_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">
                Assigned Date
              </label>
              <input
                type="date"
                data-testid="assign-date-input"
                className={inputCls}
                value={assignForm.assigned_date}
                onChange={(e) => setAssignForm({ ...assignForm, assigned_date: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setAssignFor(null)} className="px-4 py-2 text-sm border border-slate-200 rounded">
                Cancel
              </button>
              <button
                type="submit"
                data-testid="assign-submit-btn"
                className="px-4 py-2 text-sm bg-[#089949] hover:bg-[#067A3A] text-white rounded"
              >
                Assign
              </button>
            </div>
          </form>
        </div>
      )}
    </AdminLayout>
  );
}
